import type { RouteLocationRaw } from 'vue-router'
import { INVENTORY_REFERENCE_TYPE } from '../../constants/InventoryReferenceType'
import { formatReportQuantity, formatReportValue } from './utils/formatReportNumbers'

/** Items Reports tab that opens a stock card modal for one summary row. */
export const MOVEMENT_SUMMARY_TAB = 'movement-summary'

/** Item, location, and dates shared by a summary row and its stock card. */
export type StockCardScope = {
  itemUuid: string
  itemName: string
  locationId: number
  locationName: string
  startDate: string
  endDate: string
  /**
   * Same branch semantics as Movement Summary:
   * omit/undefined = active branch, '' = all branches, uuid = that branch.
   */
  branchUuid?: string
}

/** Quantity/value pair returned on the stock card header. */
export type StockCardBalance = {
  quantity: string
  value: string
}

/** One paged period entry from GET reports/inventory-stock-card. */
export type StockCardEntry = {
  effective_at: string | null
  created_at: string | null
  created_by?: { uuid?: string | null; name?: string | null } | null
  type: string | null
  reference: string | null
  reference_type: number | null
  source_uuid: string | null
  quantity_in: string
  quantity_out: string
  unit_cost: string | null
  cost_value: string
  selling_value: string | null
  balance_qty: string
  balance_value: string
}

/** Flat table row after display formatting. */
export type StockCardTableRow = StockCardEntry & {
  created_label: string
  created_by_label: string
  transaction_date_label: string
  type_label: string
  reference_label: string
  quantity_in_display: string
  quantity_out_display: string
  unit_cost_display: string
  cost_value_display: string
  selling_value_display: string
  balance_qty_display: string
  balance_value_display: string
  can_view_source: boolean
}

export type InventoryStockCardParams = {
  item_uuid: string
  location_id: number
  start_date: string
  end_date: string
  /**
   * Omit for the active branch.
   * Empty string asks for every branch (`branch_uuid=`), which needs multi-branch access.
   */
  branch_uuid?: string | null
  page?: number
  per_page?: number
  batch_id?: number | null
  serial_id?: number | null
}

export type InventoryStockCardResponse = {
  data: StockCardEntry[]
  item?: { uuid?: string; name?: string; unit_name?: string | null } | null
  location?: { id?: number; name?: string } | null
  period?: { start?: string; end?: string } | null
  opening?: StockCardBalance | null
  closing?: StockCardBalance | null
  meta?: {
    current_page?: number
    per_page?: number
    total?: number
  }
  current_page?: number
  total?: number
}

/** Query string for GET reports/inventory-stock-card. */
export function buildInventoryStockCardQuery(params: InventoryStockCardParams): string {
  const query = new URLSearchParams({
    item_uuid: params.item_uuid,
    location_id: String(params.location_id),
    start_date: params.start_date,
    end_date: params.end_date,
  })

  // Match Movement Summary: omit for active branch, empty string for all branches.
  if (params.branch_uuid === '') {
    query.set('branch_uuid', '')
  } else if (params.branch_uuid) {
    query.set('branch_uuid', params.branch_uuid)
  }

  if (params.page != null) query.set('page', String(params.page))
  if (params.per_page != null) query.set('per_page', String(params.per_page))
  if (params.batch_id != null) query.set('batch_id', String(params.batch_id))
  if (params.serial_id != null) query.set('serial_id', String(params.serial_id))

  return query.toString()
}

export function readStockCardPage(
  payload: InventoryStockCardResponse | undefined,
  requestedPage: number,
  rowCount: number,
): { page: number; total: number } {
  return {
    page: payload?.current_page ?? payload?.meta?.current_page ?? requestedPage,
    total: payload?.total ?? payload?.meta?.total ?? rowCount,
  }
}

const amount = (value: string | undefined | null): string => (value == null || value === '' ? '0' : value)

/** Normalize API timestamps to "YYYY-MM-DD" + optional "HH:mm:ss" without inventing a timezone. */
function parseStockCardTimestamp(value: string | null | undefined): { date: string; time?: string } | null {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null

  const normalized = trimmed
    .replace('T', ' ')
    .replace(/Z$/i, '')
    .replace(/\.\d+$/, '')
  const match = normalized.match(/^(\d{4}-\d{2}-\d{2})(?:\s+(\d{2}:\d{2}:\d{2}))?/)
  if (!match) return null

  return match[2] ? { date: match[1], time: match[2] } : { date: match[1] }
}

/** Full datetime for Created (created_at), e.g. 2026-10-01 04:35:43. */
export function formatStockCardDateTime(value: string | null | undefined): string {
  const parts = parseStockCardTimestamp(value)
  if (!parts) return value?.trim() ? value.trim() : '—'
  return parts.time ? `${parts.date} ${parts.time}` : parts.date
}

/** Date-only for Transaction Date (effective_at), e.g. 2026-10-01. */
export function formatStockCardDate(value: string | null | undefined): string {
  const parts = parseStockCardTimestamp(value)
  if (!parts) return value?.trim() ? value.trim() : '—'
  return parts.date
}

/** Whether this entry has a navigable source document in the app. */
export function canViewStockCardSource(entry: Pick<StockCardEntry, 'reference_type' | 'source_uuid'>): boolean {
  if (!entry.source_uuid) return false

  return (
    entry.reference_type === INVENTORY_REFERENCE_TYPE.SALE || entry.reference_type === INVENTORY_REFERENCE_TYPE.TRANSFER
  )
}

/** Route for View Source when navigation (not an in-page modal) is required. */
export function stockCardSourceRoute(
  entry: Pick<StockCardEntry, 'reference_type' | 'source_uuid'>,
): RouteLocationRaw | null {
  if (!entry.source_uuid || !canViewStockCardSource(entry)) return null

  // Sales open InvoiceDetailsModal in the stock card UI — no route navigation.
  if (entry.reference_type === INVENTORY_REFERENCE_TYPE.SALE) return null

  if (entry.reference_type === INVENTORY_REFERENCE_TYPE.TRANSFER) {
    return { name: 'inventory-transfer-detail', params: { uuid: entry.source_uuid } }
  }

  return null
}

/** Copy API amounts onto display fields. Do not recompute balances. */
export function toStockCardTableRow(entry: StockCardEntry): StockCardTableRow {
  return {
    ...entry,
    created_label: formatStockCardDateTime(entry.created_at),
    created_by_label: entry.created_by?.name?.trim() || '—',
    transaction_date_label: formatStockCardDate(entry.effective_at),
    type_label: entry.type?.trim() || '—',
    reference_label: entry.reference?.trim() || '—',
    quantity_in_display: formatReportQuantity(entry.quantity_in),
    quantity_out_display: formatReportQuantity(entry.quantity_out),
    unit_cost_display: entry.unit_cost == null ? '—' : formatReportValue(entry.unit_cost),
    cost_value_display: formatReportValue(entry.cost_value),
    selling_value_display: entry.selling_value == null ? '—' : formatReportValue(entry.selling_value),
    balance_qty_display: formatReportQuantity(entry.balance_qty),
    balance_value_display: formatReportValue(entry.balance_value),
    can_view_source: canViewStockCardSource(entry),
  }
}

/** Present opening/closing for the summary strip. */
export function presentStockCardBalance(balance: StockCardBalance | null | undefined): StockCardBalance {
  return {
    quantity: formatReportQuantity(amount(balance?.quantity)),
    value: formatReportValue(amount(balance?.value)),
  }
}
